import React from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronLeft, ChevronRight } from '@videoblocks/react-icons';

import usePagination from '../hooks/usePagination';
import { ELLIPSIS } from '../utils/pagination';
import EllipsisSeparator from './Pagination/EllipsisSeparator';
import PaginationButton from './Pagination/PaginationButton';

export interface PaginationProps {
  activePage: number;
  disabled?: boolean;
  getHref?: (pageNumber: number) => string;
  maxButtons: number;
  onPageSelect?: (pageNumber: number) => void;
  prefix?: string;
  terminalButtons: number;
  totalPages: number;
}

const Pagination = ({
  activePage,
  disabled = false,
  getHref = () => undefined,
  maxButtons,
  onPageSelect = () => undefined,
  prefix = '',
  terminalButtons,
  totalPages,
}: PaginationProps) => {
  const pages = usePagination({
    activePage,
    maxButtons,
    terminalButtons,
    totalPages,
  });

  if (pages.length === 0) {
    return <></>;
  }

  const getPageId = (page: number) => {
    return !!prefix ? `${prefix}-page-${page}` : `page-${page}`;
  };

  const getEllipsisId = (index: number) => {
    return !!prefix ? `${prefix}-ellipsis-${index}` : `ellipsis-${index}`;
  };

  const getPreviousPageId = () => {
    return !!prefix ? `${prefix}-previous-page` : 'previous-page';
  };

  const getNextPageId = () => {
    return !!prefix ? `${prefix}-next-page` : 'next-page';
  };

  const goToPage = (
    event: React.MouseEvent | React.KeyboardEvent,
    pageNumber: number
  ) => {
    event.preventDefault();
    onPageSelect(pageNumber);
  };

  const getClassName = (page: number) => {
    if (page !== activePage) return '';
    if (page === totalPages) return 'rounded-r-lg';
    if (page === 1) return 'rounded-l-lg';
    return '';
  };

  const PreviousPage = () => (
    <PaginationButton
      id={getPreviousPageId()}
      className="rounded-1-md font-bold border-r border-gray-400"
      href={getHref(activePage - 1)}
      onClick={(event) => goToPage(event, activePage - 1)}
      disabled={disabled}
    >
      <ChevronLeft
        className={twMerge(
          'w-4 h-4 fill-current',
          disabled ? 'text-gray-500' : 'text-blue-700'
        )}
      />
      <span className="sr-only">Previous Page</span>
    </PaginationButton>
  );

  const NextPage = () => (
    <PaginationButton
      id={getNextPageId()}
      disabled={disabled}
      className="rounded-r-md font-bold border-l border-gray-400"
      href={getHref(activePage + 1)}
      onClick={(event) => goToPage(event, activePage + 1)}
    >
      <ChevronRight
        className={twMerge(
          'w-4 h-4 fill-current',
          disabled ? 'text-gray-500' : 'text-blue-700'
        )}
      />
      <span className="sr-only">Next Page</span>
    </PaginationButton>
  );

  return (
    <div className="flex items-center justify-center w-full my-8">
      <div className="flex-nowrap w-max flex items-center justify-center border border-gray-400 border-solid rounded-md">
        {activePage > 1 && <PreviousPage />}
        {pages.map((page, index) => {
          const id = getPageId(page);

          return page === ELLIPSIS ? (
            <EllipsisSeparator key={getEllipsisId(index)} disabled={disabled} />
          ) : (
            <PaginationButton
              key={id}
              id={id}
              disabled={disabled}
              isActive={activePage === page}
              className={getClassName(page)}
              href={getHref(page)}
              onClick={(event) => goToPage(event, page)}
            >
              {page}
            </PaginationButton>
          );
        })}
        {activePage < totalPages && <NextPage />}
      </div>
    </div>
  );
};

export default Pagination;
